"use client";

import React, { useState } from 'react';
import { useAccessKey, useAccessGuard } from './hooks';
import { ValidationResult } from './types';

/**
 * Props for AccessKeyValidator component
 */
export interface AccessKeyValidatorProps {
  /** Initial key value */
  initialKey?: string;
  /** Placeholder text for input */
  placeholder?: string;
  /** Custom validation button text */
  validateButtonText?: string;
  /** Custom clear button text */
  clearButtonText?: string;
  /** Show clear button */
  showClearButton?: boolean;
  /** Auto-validate on input change */
  autoValidate?: boolean;
  /** Debounce delay for auto-validation (ms) */
  debounceDelay?: number;
  /** Custom CSS classes */
  className?: string;
  /** Called when validation completes */
  onValidation?: (result: ValidationResult) => void;
  /** Called when key changes */
  onKeyChange?: (key: string) => void;
  /** Disable the input */
  disabled?: boolean;
}

/**
 * Component for validating access keys with built-in UI
 */
export function AccessKeyValidator({
  initialKey = '',
  placeholder = 'Enter your access key...',
  validateButtonText = 'Validate',
  clearButtonText = 'Clear',
  showClearButton = true,
  autoValidate = false,
  debounceDelay = 500,
  className = '',
  onValidation,
  onKeyChange,
  disabled = false,
}: AccessKeyValidatorProps) {
  const [keyValue, setKeyValue] = useState(initialKey);
  const [debounceTimer, setDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const { result, loading, error, validate, clear } = useAccessKey(undefined, {
    autoValidate: false,
  });

  const handleKeyChange = (value: string) => {
    setKeyValue(value);
    onKeyChange?.(value);

    if (autoValidate && value.trim()) {
      // Clear existing timer
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }

      // Set new timer
      const timer = setTimeout(() => {
        handleValidate(value);
      }, debounceDelay);

      setDebounceTimer(timer);
    }
  };

  const handleValidate = async (key?: string) => {
    const keyToValidate = key || keyValue;
    if (!keyToValidate.trim()) return;

    const result = await validate(keyToValidate);
    onValidation?.(result);
  };

  const handleClear = () => {
    setKeyValue('');
    clear();
    onKeyChange?.('');

    if (debounceTimer) {
      clearTimeout(debounceTimer);
      setDebounceTimer(null);
    }
  };

  const getResultStyle = () => {
    if (!result) return '';
    return result.valid
      ? 'border-green-500 bg-green-50'
      : 'border-red-500 bg-red-50';
  };

  const getResultText = () => {
    if (loading) return 'Validating...';
    if (!result) return '';

    if (result.valid) {
      return result.cached
        ? '✅ Valid (cached)'
        : '✅ Valid access key';
    }

    return `❌ ${result.error || 'Invalid access key'}`;
  };

  const getResultTextColor = () => {
    if (loading) return 'text-blue-600';
    if (!result) return '';
    return result.valid ? 'text-green-700' : 'text-red-700';
  };

  return (
    <div className={`softlock-validator ${className}`}>
      <div className="flex flex-col space-y-3">
        <div className="flex space-x-2">
          <input
            type="text"
            value={keyValue}
            onChange={(e) => handleKeyChange(e.target.value)}
            placeholder={placeholder}
            disabled={disabled || loading}
            className={`flex-1 px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${getResultStyle()}`}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !autoValidate) {
                handleValidate();
              }
            }}
          />

          {!autoValidate && (
            <button
              onClick={() => handleValidate()}
              disabled={disabled || loading || !keyValue.trim()}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Validating...' : validateButtonText}
            </button>
          )}

          {showClearButton && (keyValue || result) && (
            <button
              onClick={handleClear}
              disabled={disabled}
              className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 disabled:opacity-50"
            >
              {clearButtonText}
            </button>
          )}
        </div>

        {(result || loading) && (
          <div className={`text-sm font-medium ${getResultTextColor()}`}>
            {getResultText()}
          </div>
        )}

        {error && !result && (
          <div className="text-sm text-red-600">
            Error: {error}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * Props for AccessGuard component
 */
export interface AccessGuardProps {
  /** Access key to validate */
  keyValue?: string;
  /** Children to render when access is granted */
  children: React.ReactNode;
  /** Component to render when access is denied */
  fallback?: React.ReactNode;
  /** Component to render while loading */
  loadingComponent?: React.ReactNode;
  /** URL to redirect to when access is denied */
  redirectUrl?: string;
  /** Called when access is denied */
  onUnauthorized?: () => void;
  /** Custom CSS classes */
  className?: string;
}

/**
 * Component that conditionally renders children based on access key validation
 */
export function AccessGuard({
  keyValue,
  children,
  fallback,
  loadingComponent,
  redirectUrl,
  onUnauthorized,
  className,
}: AccessGuardProps) {
  const { isAuthorized, loading } = useAccessGuard(keyValue, {
    redirectUrl,
    onUnauthorized,
  });

  if (loading) {
    if (loadingComponent) {
      return <div className={className}>{loadingComponent}</div>;
    }

    return (
      <div className={`flex items-center justify-center p-4 ${className || ''}`}>
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Verifying access...</span>
      </div>
    );
  }

  if (isAuthorized === false) {
    if (fallback) {
      return <div className={className}>{fallback}</div>;
    }

    return (
      <div className={`p-4 text-center bg-gray-50 border border-gray-200 rounded-md ${className || ''}`}>
        <div className="text-red-500 text-lg mb-2">🔒</div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">Access Denied</h3>
        <p className="text-gray-500">
          You don't have permission to view this content.
        </p>
      </div>
    );
  }

  if (isAuthorized === true) {
    return <div className={className}>{children}</div>;
  }

  // isAuthorized is null (no key provided or unknown state)
  return (
    <div className={`p-4 text-center bg-gray-50 border border-gray-200 rounded-md ${className || ''}`}>
      <div className="text-gray-400 text-lg mb-2">🔑</div>
      <h3 className="text-lg font-semibold text-gray-700 mb-2">
        Access Key Required
      </h3>
      <p className="text-gray-500">
        Please provide a valid access key to view this content.
      </p>
    </div>
  );
}/**
 * Props for AccessStatus component
 */
export interface AccessStatusProps {
  /** Access key to check */
  keyValue?: string;
  /** Show detailed key information */
  showDetails?: boolean;
  /** Auto-refresh interval (ms) */
  refreshInterval?: number;
  /** Custom CSS classes */
  className?: string;
}

/**
 * Component that displays the current access status
 */
export function AccessStatus({
  keyValue,
  showDetails = false,
  refreshInterval,
  className,
}: AccessStatusProps) {
  const { result, loading, error, retry } = useAccessKey(keyValue, {
    autoValidate: !!keyValue,
    refetchInterval: refreshInterval,
  });

  if (!keyValue) {
    return (
      <div className={`p-4 text-center bg-gray-50 border border-gray-200 rounded-md ${className || ''}`}>
        <div className="text-gray-400 text-lg mb-2">🔑</div>
        <p className="text-gray-500">No access key provided</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 ${className || ''}`}>
        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
        <span className="ml-2 text-gray-600">Checking access...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-md ${className || ''}`}>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-red-500 text-lg mb-1">❌</div>
            <h3 className="text-sm font-semibold text-red-800">Validation Error</h3>
            <p className="text-red-600 text-sm">{error}</p>
          </div>
          <button
            onClick={retry}
            className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded hover:bg-red-200"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!result) {
    return (
      <div className={`p-4 text-center bg-gray-50 border border-gray-200 rounded-md ${className || ''}`}>
        <div className="text-gray-400 text-lg mb-2">❓</div>
        <p className="text-gray-500">No validation result</p>
      </div>
    );
  }

  if (!result.valid) {
    return (
      <div className={`p-4 bg-red-50 border border-red-200 rounded-md ${className || ''}`}>
        <div className="text-red-500 text-lg mb-2">❌</div>
        <h3 className="text-sm font-semibold text-red-800 mb-1">Access Denied</h3>
        <p className="text-red-600 text-sm">{result.error || 'Invalid access key'}</p>
      </div>
    );
  }

  // Valid access key
  return (
    <div className={`p-4 bg-green-50 border border-green-200 rounded-md ${className || ''}`}>
      <div className="text-green-500 text-lg mb-2">✅</div>
      <h3 className="text-sm font-semibold text-green-800 mb-2">Access Granted</h3>

      {showDetails && result.key && (
        <div className="space-y-2 text-sm">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="font-medium text-green-700">Status:</span>
              <span className="ml-2 text-green-600">{result.key.status}</span>
            </div>
            {result.key.discord_tag && (
              <div>
                <span className="font-medium text-green-700">User:</span>
                <span className="ml-2 text-green-600">{result.key.discord_tag}</span>
              </div>
            )}
            <div>
              <span className="font-medium text-green-700">Created:</span>
              <span className="ml-2 text-green-600">
                {new Date(result.key.created_at).toLocaleDateString()}
              </span>
            </div>
            {result.key.expires_at && (
              <div>
                <span className="font-medium text-green-700">Expires:</span>
                <span className="ml-2 text-green-600">
                  {new Date(result.key.expires_at).toLocaleDateString()}
                </span>
              </div>
            )}
          </div>

          {result.cached && (
            <div className="text-xs text-green-600 mt-2">
              ℹ️ Cached result
            </div>
          )}
        </div>
      )}
    </div>
  );
}